import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Check, X } from 'lucide-react';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Card, Input, Logo, cn } from '@techsofit/ui';
import {
  accountLogout,
  checkSlug,
  createOrg,
  fetchAccountMe,
  fetchTenant,
  requestCode,
  switchProfile,
  verifyCode,
  verifyLink,
  type AccountMe,
  type ProfileView,
  type TenantBranding,
} from '../lib/accountApi';

/**
 * The gym front door. Gym-web is gym-only, so there are exactly two paths:
 *   • Log in   — access the gym you already own or work at (email + code).
 *   • Sign up  — register a new gym (gym name + your name + email + code).
 * Both are passwordless: one email gets a 6-digit code, verifying it signs you in.
 * Individuals/coaches sign up in the separate personal app, not here. /start and
 * /login redirect to this page; /signup opens it in sign-up mode.
 */
/**
 * The verify choreography: idle (typing) -> verifying (a line traces around each
 * box) -> merging (boxes collapse into one) -> circling (the line keeps going
 * around the merged badge) -> success (it becomes a tick), then auto-advance.
 * A wrong code routes to 'error' instead: the badge shakes red with a cross, then
 * the boxes return so they can retry.
 */
type VerifyPhase = 'idle' | 'verifying' | 'merging' | 'circling' | 'success' | 'error';
type Mode = 'login' | 'signup';
type Step = 'form' | 'code' | 'link' | 'noGym' | 'createGym' | 'chooseSlug' | 'notMember' | 'noAccount' | 'chooseGym';

/**
 * Phones often reload this page when someone hops to their inbox for the code and back. We keep
 * the "code sent" step (email + mode) for as long as the code is valid, so they land back on the
 * code boxes — not on an empty email field that would issue a new code and void the one they have.
 */
const PENDING_KEY = 'tsf.account-otp.pending';
const CODE_TTL_MS = 10 * 60 * 1000;
interface PendingCode {
  email: string;
  mode: Mode;
  name?: string;
  gymName?: string;
  sentAt: number;
}
function readPendingCode(): PendingCode | null {
  try {
    const raw = window.localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as PendingCode;
    if (!p?.email || typeof p.sentAt !== 'number' || Date.now() - p.sentAt > CODE_TTL_MS) {
      window.localStorage.removeItem(PENDING_KEY);
      return null;
    }
    return p;
  } catch {
    return null;
  }
}
function writePendingCode(p: PendingCode) {
  try {
    window.localStorage.setItem(PENDING_KEY, JSON.stringify(p));
  } catch {
    /* private mode — we just lose the safety net */
  }
}
function clearPendingCode() {
  try {
    window.localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}
/** Pull `?lt=` (the email's one-tap token) off the URL and clean the address bar. */
function takeLinkToken(): string | null {
  const url = new URL(window.location.href);
  const token = url.searchParams.get('lt');
  if (!token) return null;
  url.searchParams.delete('lt');
  window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
  return token;
}

/** Client-side slug preview (the backend re-derives + checks authoritatively). */
const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

// Production feel: snappy but every stage still reads. Bump these up to slow-mo it.
const TRACE_MIN_MS = 600; // line tracing each box (also the floor on the API call)
const MERGE_MS = 260; // boxes merge into one
const CIRCLE_MS = 600; // the line circles the merged badge
const TICK_HOLD_MS = 500; // hold on the tick before moving on
const ERROR_HOLD_MS = 700; // hold on the red cross before the boxes return
const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** The gym (owner/staff) profile on this account, if any. STAFF = lives in the gym app. */
function gymProfile(m: AccountMe) {
  return m.profiles.find((p) => p.kind === 'STAFF');
}

/** All gyms this account owns/works at (one entry per gym they can manage). */
function gymProfiles(m: AccountMe): ProfileView[] {
  return m.profiles.filter((p) => p.kind === 'STAFF');
}

/** Slug of the account's active context (the gym just created/switched into). */
function activeGymSlug(m: AccountMe): string | null {
  return m.profiles.find((p) => p.id === m.activeProfileId)?.slug ?? null;
}

/** True when the account's active context is a gym membership (a member, not staff). */
function activeIsMember(m: AccountMe): boolean {
  return m.profiles.find((p) => p.id === m.activeProfileId)?.kind === 'MEMBERSHIP';
}

export function AccountAuthPage({ initialMode = 'login' }: { initialMode?: Mode } = {}) {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [step, setStep] = useState<Step>('form');
  const [booting, setBooting] = useState(true);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [gymName, setGymName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verifyPhase, setVerifyPhase] = useState<VerifyPhase>('idle');
  const [me, setMe] = useState<AccountMe | null>(null);
  const [tenant, setTenant] = useState<TenantBranding | null>(null);
  const [tenantLoaded, setTenantLoaded] = useState(false);
  // Subdomain picker (the last signup step): chosen slug + its live availability.
  const [slug, setSlug] = useState('');
  const [slugCheck, setSlugCheck] = useState<{ state: 'idle' | 'checking' | 'ok' | 'bad'; reason?: string; suggestion?: string }>({
    state: 'idle',
  });

  // Are we on a gym subdomain (<slug>.<base>) rather than the apex? Used to tell a dead
  // gym address apart from the apex: on a subdomain with no resolved gym we show a clear
  // "not found" instead of the apex register/OTP flow (which would waste an email + code).
  const tenantBase = import.meta.env.VITE_TENANT_BASE_DOMAIN as string | undefined;
  const onGymSubdomain = !!tenantBase && window.location.hostname.endsWith(`.${tenantBase}`);
  // The gym slug from the current host (e.g. "rodney-fitness"), or null on the apex. Used to
  // auto-pick the matching gym so a subdomain login skips the chooser.
  const hostSlug = onGymSubdomain && tenantBase
    ? window.location.hostname.slice(0, -(tenantBase.length + 1))
    : null;
  // The main site (registrable domain), e.g. gyms.techsofit.com → https://techsofit.com.
  const mainSiteUrl = tenantBase
    ? `${window.location.protocol}//${tenantBase.split('.').slice(1).join('.')}`
    : '/';

  // On a gym subdomain, skin the sign-in screen with that gym's logo + name.
  useEffect(() => {
    fetchTenant().then(setTenant).catch(() => setTenant(null)).finally(() => setTenantLoaded(true));
  }, []);

  // Live-check the chosen subdomain while on the slug step (debounced).
  useEffect(() => {
    if (step !== 'chooseSlug') return;
    const s = slugify(slug);
    if (s.length < 3) {
      setSlugCheck({ state: 'bad', reason: 'Use at least 3 characters.' });
      return;
    }
    setSlugCheck({ state: 'checking' });
    const t = setTimeout(() => {
      checkSlug(s)
        .then((r) =>
          setSlugCheck(
            r.available
              ? { state: 'ok' }
              : { state: 'bad', reason: r.reason ?? 'Not available.', suggestion: r.suggestion ?? undefined },
          ),
        )
        .catch(() => setSlugCheck({ state: 'idle' }));
    }, 400);
    return () => clearTimeout(t);
  }, [slug, step]);

  // Already signed in? A gym account drops straight into the dashboard. The ?create
  // flag (from the in-app gym switcher) opens the "add a gym" form for the signed-in
  // account. A signed-in account with no gym is prompted to register one.
  useEffect(() => {
    const wantsCreate = new URLSearchParams(window.location.search).has('create');
    const linkToken = takeLinkToken();
    fetchAccountMe()
      .then(async (m) => {
        clearPendingCode();
        setMe(m);
        if (wantsCreate) {
          setGymName('');
          setStep('createGym');
          return;
        }
        if (gymProfiles(m).length > 0) {
          await enterStaff(m);
          return;
        }
        if (activeIsMember(m)) {
          window.location.assign('/me');
          return;
        }
        // Signed in, but this account has no gym/membership — offer to register one.
        setStep('noGym');
      })
      .catch(async () => {
        // Not signed in. The email's one-tap button lands here with ?lt=; otherwise, if a code
        // is still in flight (page reloaded while they fetched it), go straight back to the boxes.
        const pending = readPendingCode();
        if (linkToken) {
          await signInWithLink(linkToken, pending);
          return;
        }
        if (pending) {
          setEmail(pending.email);
          setMode(pending.mode);
          if (pending.name) setName(pending.name);
          if (pending.gymName) setGymName(pending.gymName);
          setStep('code');
        }
      })
      .finally(() => setBooting(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Where a freshly signed-in account goes: staff → gym app, member → /me, nobody → register / not-member. */
  async function routeSignedIn(m: AccountMe, wasSignup: boolean, signupGymName: string) {
    clearPendingCode();
    setMe(m);
    if (gymProfiles(m).length > 0) {
      // Attached to one or more gyms (returning owner/staff): enter, or pick if several.
      await enterStaff(m);
      return;
    }
    if (wasSignup) {
      // Brand-new gym: pick the subdomain (the final step), then create + enter.
      beginChooseSlug(signupGymName);
      return;
    }
    if (activeIsMember(m)) {
      // A gym member — the backend bridged a member-portal session; open it.
      window.location.assign('/me');
      return;
    }
    // No role: on a gym subdomain that means "not part of this gym"; on the apex, register.
    // (Host-derived, not tenant state — this also runs from the mount effect.)
    setVerifyPhase('idle');
    setStep(onGymSubdomain ? 'notMember' : 'noGym');
  }

  async function signInWithLink(token: string, pending: PendingCode | null) {
    setStep('link');
    try {
      const m = await verifyLink(token);
      await routeSignedIn(m, pending?.mode === 'signup' && !onGymSubdomain, pending?.gymName ?? '');
    } catch (err) {
      // Expired or already used: fall back to the code boxes if that code is still live,
      // else the email form, with a clear reason.
      setError(
        err instanceof ApiClientError
          ? err.message
          : 'That sign-in link has expired — request a new code.',
      );
      if (pending) {
        setEmail(pending.email);
        setMode(pending.mode);
        setStep('code');
      } else {
        setStep('form');
      }
    }
  }

  function switchMode(next: Mode) {
    setMode(next);
    setStep('form');
    setCode('');
    setError(null);
    setVerifyPhase('idle');
  }

  function leaveCodeStep() {
    clearPendingCode();
    setStep('form');
    setCode('');
    setError(null);
  }

  const [resent, setResent] = useState(false);
  async function resendCode() {
    setError(null);
    setBusy(true);
    try {
      const { otpSent } = await requestCode(email.trim(), signupMode);
      if (!otpSent) {
        leaveCodeStep();
        return;
      }
      writePendingCode({ email: email.trim(), mode, name: name.trim() || undefined, gymName: gymName.trim() || undefined, sentAt: Date.now() });
      setCode('');
      setResent(true);
      window.setTimeout(() => setResent(false), 4000);
    } catch {
      setError("Couldn't resend. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function sendCode() {
    const e = email.trim();
    if (!e.includes('@')) {
      setError('Enter a valid email.');
      return;
    }
    if (signupMode && !gymName.trim()) {
      setError('Enter your business name.');
      return;
    }
    if (signupMode && !name.trim()) {
      setError('Enter your name.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const { otpSent } = await requestCode(e, signupMode);
      // No code is sent when we don't recognise the email: on a gym subdomain that means
      // "not a member here"; on the apex login door it means "no account — sign up instead".
      // (Signup always sends, so otpSent is only false for a login attempt.)
      if (otpSent) {
        writePendingCode({ email: e, mode, name: name.trim() || undefined, gymName: gymName.trim() || undefined, sentAt: Date.now() });
      }
      setStep(otpSent ? 'code' : onGymSubdomain ? 'notMember' : 'noAccount');
    } catch {
      setError('Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    if (code.trim().length < 6) {
      setError('Enter the 6-digit code.');
      return;
    }
    setError(null);
    setBusy(true);
    setVerifyPhase('verifying');
    try {
      // Keep the boxes tracing for at least TRACE_MIN_MS so the loading is visible.
      // On sign-up we pass the name so the account is created with it.
      const [m] = await Promise.all([
        verifyCode(email.trim(), code.trim(), signupMode ? name.trim() : undefined),
        delay(TRACE_MIN_MS),
      ]);
      setVerifyPhase('merging');
      await delay(MERGE_MS);
      setVerifyPhase('circling');
      await delay(CIRCLE_MS);
      setVerifyPhase('success');
      await delay(TICK_HOLD_MS);
      await routeSignedIn(m, signupMode, gymName);
    } catch (err) {
      // Mirror the success choreography with a failure one: the badge shakes red with
      // a cross, then collapses back to the boxes so they can fix the code.
      setVerifyPhase('error');
      await delay(ERROR_HOLD_MS);
      setVerifyPhase('idle');
      setError(err instanceof ApiClientError ? err.message : "That code didn't work.");
    } finally {
      setBusy(false);
    }
  }

  function submitCreateGym() {
    if (!gymName.trim()) {
      setError('Enter your business name.');
      return;
    }
    setError(null);
    beginChooseSlug(gymName);
  }

  // Move to the subdomain picker, prefilled from the gym name.
  function beginChooseSlug(name: string) {
    setSlug(slugify(name));
    setSlugCheck({ state: 'idle' });
    setVerifyPhase('idle');
    setStep('chooseSlug');
  }

  // Create the gym with the chosen, confirmed-available subdomain, then enter it.
  async function finishCreateGym() {
    if (slugCheck.state !== 'ok') return;
    setError(null);
    setBusy(true);
    try {
      const created = await createOrg(gymName.trim(), 'GYM', slugify(slug));
      enterGymApp(activeGymSlug(created));
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Couldn't create that. Try again.");
      setBusy(false);
    }
  }

  // Route an account that owns/works at one or more gyms: prefer the gym matching the current
  // subdomain, auto-enter when there's only one, else show the chooser ("first, pick a gym").
  async function enterStaff(m: AccountMe) {
    const staff = gymProfiles(m);
    const match = hostSlug ? staff.find((p) => p.slug === hostSlug) : null;
    const target = match ?? (staff.length === 1 ? staff[0] : null);
    if (target) {
      await enterGymProfile(m, target);
      return;
    }
    setStep('chooseGym');
  }

  // Make the chosen gym profile active (if it isn't), then enter its subdomain.
  async function enterGymProfile(m: AccountMe, p: ProfileView) {
    let gymSlug = p.slug;
    if (p.id !== m.activeProfileId) {
      try {
        gymSlug = activeGymSlug(await switchProfile(p.id)) ?? gymSlug;
      } catch {
        // non-fatal — the app re-bootstraps and can still switch
      }
    }
    enterGymApp(gymSlug);
  }

  // Enter the gym app. With subdomain tenancy on (VITE_TENANT_BASE_DOMAIN set), redirect
  // to the gym's own subdomain (<slug>.<base>); otherwise a relative /dashboard. Full load
  // either way so the auth provider re-bootstraps from the gym context.
  function enterGymApp(slug: string | null) {
    const base = import.meta.env.VITE_TENANT_BASE_DOMAIN as string | undefined;
    if (slug && base) {
      const port = window.location.port ? `:${window.location.port}` : '';
      window.location.assign(`${window.location.protocol}//${slug}.${base}${port}/dashboard`);
    } else {
      window.location.assign('/dashboard');
    }
  }

  async function logoutToLogin() {
    try {
      await accountLogout();
    } catch {
      // ignore — we're resetting the form regardless
    }
    setMe(null);
    setGymName('');
    setName('');
    setCode('');
    switchMode('login');
  }

  // Signing up (registering a new gym) only happens on the apex. On a gym subdomain the
  // door is login-only — members are added by the gym, owners register on the apex.
  const signupMode = mode === 'signup' && !tenant;

  // Does the signed-in account already run a gym? Drives "first gym" vs "add a gym" copy.
  const hasGym = !!(me && gymProfile(me));

  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas p-4">
      <Card className="w-full max-w-sm p-6">
        {tenant ? (
          // The gym's brand leads. With no logo yet, a monogram of its name stands in —
          // never the TechSoFit mark — so the page reads as the gym's, not ours. TechSoFit
          // is credited subtly in the footer below.
          <div className="flex items-center gap-3">
            {tenant.logoUrl ? (
              <img
                src={tenant.logoUrl}
                alt=""
                className="size-11 shrink-0 rounded-card border border-line object-contain"
              />
            ) : (
              <span
                aria-hidden
                className="grid size-11 shrink-0 place-items-center rounded-card bg-dark font-display text-lg font-semibold text-on-dark"
              >
                {tenant.name.trim().charAt(0).toUpperCase()}
              </span>
            )}
            <span className="font-display text-lg font-semibold text-ink">{tenant.name}</span>
          </div>
        ) : (
          <Logo />
        )}

        {booting || step === 'link' || (onGymSubdomain && !tenantLoaded) ? (
          <p className="mt-6 text-sm text-muted">{step === 'link' ? 'Signing you in…' : 'Loading…'}</p>
        ) : onGymSubdomain && !tenant ? (
          <>
            <h1 className="mt-5 font-display text-display-sm text-ink">Business not found</h1>
            <p className="mt-1 text-sm text-muted">
              This address isn't an active business on TechSoFit. Check the link, or head to our main site.
            </p>
            <div className="mt-5">
              <a
                href={mainSiteUrl}
                className="inline-flex items-center justify-center gap-2 rounded-control bg-ink px-4 py-2.5 text-sm font-medium text-on-dark transition-colors hover:bg-ink-soft"
              >
                Go to TechSoFit <ArrowRight className="size-4" aria-hidden />
              </a>
            </div>
          </>
        ) : step === 'form' ? (
          <>
            <h1 className="mt-5 font-display text-display-sm text-ink">
              {signupMode ? 'Register your business' : tenant ? `Sign in to ${tenant.name}` : 'Log in to your business'}
            </h1>
            <p className="mt-1 text-sm text-muted">
              {signupMode
                ? "Set up your business on TechSoFit. We'll email a code to confirm, no password."
                : "Enter your email and we'll send a sign-in code, no password."}
            </p>
            <div className="mt-5 flex flex-col gap-3">
              {signupMode && (
                <>
                  <Input
                    label="Business name"
                    value={gymName}
                    onChange={(e) => setGymName(e.target.value)}
                    placeholder="Iron Peak Fitness"
                  />
                  <Input
                    label="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Tanya Moyo"
                  />
                </>
              )}
              <Input
                label="Email"
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                hint="We'll email you a 6-digit code."
                onKeyDown={(e) => e.key === 'Enter' && sendCode()}
              />
              {error && <p className="text-sm font-medium text-bad-ink">{error}</p>}
              <Button onClick={sendCode} loading={busy} block>
                {signupMode ? 'Create account' : 'Send code'}{' '}
                <ArrowRight className="size-4" aria-hidden />
              </Button>
            </div>
            {/* Register-a-gym only on the apex; a gym subdomain is login-only. */}
            {!tenant && (
              <p className="mt-5 text-center text-sm text-muted">
                {signupMode ? (
                  <>
                    Already have an account?{' '}
                    <button
                      type="button"
                      onClick={() => switchMode('login')}
                      className="font-medium text-ink hover:underline"
                    >
                      Log in
                    </button>
                  </>
                ) : (
                  <>
                    New here?{' '}
                    <button
                      type="button"
                      onClick={() => switchMode('signup')}
                      className="font-medium text-ink hover:underline"
                    >
                      Register your business
                    </button>
                  </>
                )}
              </p>
            )}
          </>
        ) : step === 'code' ? (
          <>
            <h1 className="mt-5 font-display text-display-sm text-ink">Check your email</h1>
            <p className="mt-1 text-sm text-muted">
              Enter the 6-digit code we sent to{' '}
              <span className="font-medium text-ink">{email.trim()}</span>.
            </p>
            <div className="mt-6 flex flex-col gap-4">
              <OtpStage phase={verifyPhase} value={code} onChange={setCode} onSubmit={verify} />
              {verifyPhase === 'idle' ? (
                <>
                  {error && <p className="text-sm font-medium text-bad-ink">{error}</p>}
                  <Button onClick={verify} block>
                    Verify
                  </Button>
                  <p className="text-center text-xs text-muted">
                    Tip: the email also has a <span className="font-medium text-ink">Sign me in</span> button — one tap, no typing.
                  </p>
                  <div className="flex items-center justify-center gap-4 text-sm">
                    <button
                      type="button"
                      onClick={resendCode}
                      disabled={busy}
                      className="text-muted hover:text-ink disabled:opacity-50"
                    >
                      {resent ? 'Sent — check your inbox' : 'Resend code'}
                    </button>
                    <span aria-hidden className="text-muted/50">·</span>
                    <button type="button" onClick={leaveCodeStep} className="text-muted hover:text-ink">
                      Use a different email
                    </button>
                  </div>
                </>
              ) : (
                <p className={cn('text-center text-sm font-medium', verifyPhase === 'error' ? 'text-bad-ink' : 'text-ink')}>
                  {verifyPhase === 'verifying'
                    ? 'Checking your code…'
                    : verifyPhase === 'success'
                      ? "You're in"
                      : verifyPhase === 'error'
                        ? "That code didn't work"
                        : 'Almost there…'}
                </p>
              )}
            </div>
          </>
        ) : step === 'createGym' ? (
          <>
            <h1 className="mt-5 font-display text-display-sm text-ink">
              {hasGym ? 'Add a business' : 'Name your business'}
            </h1>
            <p className="mt-1 text-sm text-muted">
              {hasGym
                ? 'Register another business on your account. Free for a month, then a plan.'
                : "One more step. Name your business to finish setting up. Free for a month, then a plan."}
            </p>
            <div className="mt-5 flex flex-col gap-3">
              <Input
                label="Business name"
                value={gymName}
                onChange={(e) => setGymName(e.target.value)}
                placeholder="Iron Peak Fitness"
                onKeyDown={(e) => e.key === 'Enter' && submitCreateGym()}
              />
              {error && <p className="text-sm font-medium text-bad-ink">{error}</p>}
              <Button onClick={submitCreateGym} loading={busy} block>
                Continue <ArrowRight className="size-4" aria-hidden />
              </Button>
              <button
                type="button"
                onClick={() => (hasGym ? enterGymApp(gymProfile(me!)?.slug ?? null) : setStep('noGym'))}
                className="text-sm text-muted hover:text-ink"
              >
                ← Back
              </button>
            </div>
          </>
        ) : step === 'chooseSlug' ? (
          <>
            <h1 className="mt-5 font-display text-display-sm text-ink">Pick your web address</h1>
            <p className="mt-1 text-sm text-muted">
              This is the link you and your members use to sign in. Pick it carefully. It can't be changed later.
            </p>
            <div className="mt-5 flex flex-col gap-3">
              <div>
                <div className="flex items-stretch overflow-hidden rounded-control border border-line bg-elevated focus-within:border-accent-strong">
                  <input
                    value={slug}
                    onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                    onKeyDown={(e) => e.key === 'Enter' && finishCreateGym()}
                    placeholder="iron-peak"
                    autoFocus
                    aria-label="Your web address"
                    className="min-w-0 flex-1 bg-transparent px-3 py-2.5 text-ink outline-none"
                  />
                  <span className="flex items-center whitespace-nowrap bg-surface px-3 text-sm text-muted">
                    .{tenantBase}
                  </span>
                </div>
                <div className="mt-2 min-h-[1.25rem] text-sm">
                  {slugCheck.state === 'checking' && <span className="text-muted">Checking availability…</span>}
                  {slugCheck.state === 'ok' && (
                    <span className="inline-flex items-center gap-1 font-medium text-ok-ink">
                      <Check className="size-4" aria-hidden /> {slugify(slug)}.{tenantBase} is available
                    </span>
                  )}
                  {slugCheck.state === 'bad' && (
                    <span className="font-medium text-bad-ink">
                      {slugCheck.reason}
                      {slugCheck.suggestion && (
                        <>
                          {' '}
                          <button
                            type="button"
                            onClick={() => setSlug(slugCheck.suggestion!)}
                            className="font-semibold underline underline-offset-2"
                          >
                            Use {slugCheck.suggestion}
                          </button>
                        </>
                      )}
                    </span>
                  )}
                </div>
              </div>
              {error && <p className="text-sm font-medium text-bad-ink">{error}</p>}
              <Button onClick={finishCreateGym} loading={busy} disabled={slugCheck.state !== 'ok' || busy} block>
                Create business <ArrowRight className="size-4" aria-hidden />
              </Button>
            </div>
          </>
        ) : step === 'chooseGym' ? (
          <>
            <h1 className="mt-5 font-display text-display-sm text-ink">Choose a business</h1>
            <p className="mt-1 text-sm text-muted">You're on more than one business. Pick the one to open.</p>
            <div className="mt-5 flex flex-col gap-2">
              {(me ? gymProfiles(me) : []).map((p) => (
                <button
                  key={p.id}
                  type="button"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    await enterGymProfile(me!, p);
                  }}
                  className="flex items-center justify-between gap-3 rounded-control border border-line bg-elevated px-4 py-3 text-left transition hover:border-line-strong disabled:opacity-50"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-ink">{p.orgName ?? 'Business'}</span>
                    <span className="block text-xs text-muted">
                      {p.role === 'OWNER' ? 'Owner' : 'Staff'}
                      {p.coach ? ' · Coach' : ''}
                      {p.slug ? ` · ${p.slug}.${tenantBase}` : ''}
                    </span>
                  </span>
                  <ArrowRight className="size-4 shrink-0 text-muted" aria-hidden />
                </button>
              ))}
            </div>
            <div className="mt-5 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  setGymName('');
                  setError(null);
                  setStep('createGym');
                }}
                className="text-sm font-medium text-ink hover:underline"
              >
                Register another business
              </button>
              <button type="button" onClick={logoutToLogin} className="text-sm text-muted hover:text-ink">
                Use a different email
              </button>
            </div>
          </>
        ) : step === 'noGym' ? (
          <>
            <h1 className="mt-5 font-display text-display-sm text-ink">No business on this account</h1>
            <p className="mt-1 text-sm text-muted">
              This email isn't linked to a business yet. Register your business to get started. A member
              of a gym or club? They add you, you don't sign up here.
            </p>
            <div className="mt-5 flex flex-col gap-3">
              <Button
                onClick={() => {
                  setGymName('');
                  setError(null);
                  setStep('createGym');
                }}
                block
              >
                Register your business <ArrowRight className="size-4" aria-hidden />
              </Button>
              <button
                type="button"
                onClick={logoutToLogin}
                className="text-sm text-muted hover:text-ink"
              >
                Use a different email
              </button>
            </div>
          </>
        ) : step === 'notMember' ? (
          <>
            <h1 className="mt-5 font-display text-display-sm text-ink">
              We couldn't find you at {tenant?.name ?? 'this gym'}
            </h1>
            <p className="mt-1 text-sm text-muted">
              This email isn't on {tenant?.name ?? 'this gym'}'s list yet. If {tenant?.name ?? 'this gym'} is
              your gym or personal trainer, ask them to add you and you'll be able to sign in here.
            </p>
            <div className="mt-5">
              <button
                type="button"
                onClick={() => {
                  setEmail('');
                  setCode('');
                  setError(null);
                  setStep('form');
                }}
                className="text-sm font-medium text-ink hover:underline"
              >
                Use a different email
              </button>
            </div>
          </>
        ) : step === 'noAccount' ? (
          <>
            <h1 className="mt-5 font-display text-display-sm text-ink">No account with this email</h1>
            <p className="mt-1 text-sm text-muted">
              We couldn't find an account for {email || 'that email'}. Run a fitness business? Register it
              to get started. A member of a gym or club? They add you, you don't sign up here.
            </p>
            <div className="mt-5 flex flex-col gap-3">
              <Button onClick={() => switchMode('signup')} block>
                Register your business <ArrowRight className="size-4" aria-hidden />
              </Button>
              <button
                type="button"
                onClick={() => {
                  setEmail('');
                  setCode('');
                  setError(null);
                  setStep('form');
                }}
                className="text-sm text-muted hover:text-ink"
              >
                Use a different email
              </button>
            </div>
          </>
        ) : null}

        {/* On a gym-branded subdomain, credit TechSoFit subtly under the card. */}
        {tenant && !booting && (
          <p className="mt-6 text-center text-xs text-muted">Powered by TechSoFit</p>
        )}
      </Card>
    </div>
  );
}

/**
 * Segmented 6-digit OTP entry that doubles as the verify animation surface. Type
 * anywhere and digits flow box to box (paste + backspace handled). On verify, a
 * line traces each box, then the boxes merge into one badge that keeps circling
 * and resolves into a tick.
 */
function OtpStage({
  phase,
  value,
  onChange,
  onSubmit,
}: {
  phase: VerifyPhase;
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const editable = phase === 'idle';
  const showBadge = phase === 'merging' || phase === 'circling' || phase === 'success' || phase === 'error';
  const chars = Array.from({ length: 6 }, (_, i) => value[i] ?? '');

  useEffect(() => {
    if (phase === 'idle') refs.current[0]?.focus();
  }, [phase]);

  const focusBox = (i: number) => {
    const el = refs.current[i];
    if (el) {
      el.focus();
      el.select();
    }
  };
  const replaceAt = (i: number, ch: string) => {
    const idx = Math.min(i, value.length);
    onChange((value.slice(0, idx) + ch + value.slice(idx + 1)).replace(/\D/g, '').slice(0, 6));
  };
  const clearAt = (i: number) => onChange((value.slice(0, i) + value.slice(i + 1)).slice(0, 6));

  function onInput(i: number, e: React.ChangeEvent<HTMLInputElement>) {
    const d = e.target.value.replace(/\D/g, '');
    if (!d) {
      clearAt(i);
      return;
    }
    replaceAt(i, d[d.length - 1]);
    if (i < 5) focusBox(i + 1);
  }
  function onKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (chars[i]) clearAt(i);
      else if (i > 0) {
        focusBox(i - 1);
        clearAt(i - 1);
      }
    } else if (e.key === 'ArrowLeft' && i > 0) {
      e.preventDefault();
      focusBox(i - 1);
    } else if (e.key === 'ArrowRight' && i < 5) {
      e.preventDefault();
      focusBox(i + 1);
    } else if (e.key === 'Enter' && value.length === 6) {
      onSubmit();
    }
  }
  function onPaste(e: React.ClipboardEvent) {
    const d = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (d) {
      e.preventDefault();
      onChange(d);
      focusBox(Math.min(d.length, 5));
    }
  }

  return (
    <div className="relative h-14">
      <OtpKeyframes />
      {/* the six boxes (fade + collapse together as they "merge") */}
      <div
        className={cn(
          'absolute inset-0 flex items-center justify-center gap-2 transition-all duration-500',
          showBadge ? 'scale-90 gap-0 opacity-0' : 'opacity-100',
        )}
      >
        {chars.map((c, i) => (
          <div key={i} className="relative h-12 w-10">
            <input
              ref={(el) => {
                refs.current[i] = el;
              }}
              value={c}
              onChange={(e) => onInput(i, e)}
              onKeyDown={(e) => onKeyDown(i, e)}
              onPaste={onPaste}
              onFocus={(e) => e.target.select()}
              disabled={!editable}
              inputMode="numeric"
              maxLength={1}
              aria-label={`Digit ${i + 1}`}
              className={cn(
                'size-full rounded-control border bg-elevated text-center font-display text-xl text-ink outline-none transition',
                phase === 'verifying'
                  ? 'border-transparent'
                  : 'border-line focus:border-accent-strong focus:ring-2 focus:ring-accent-strong/40',
              )}
            />
            {phase === 'verifying' && (
              <svg viewBox="0 0 40 48" className="pointer-events-none absolute inset-0 text-accent-strong">
                <rect
                  x="1.5"
                  y="1.5"
                  width="37"
                  height="45"
                  rx="11"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  pathLength={100}
                  strokeDasharray="28 72"
                  style={{ animation: 'otp-trace 1s linear infinite' }}
                />
              </svg>
            )}
          </div>
        ))}
      </div>

      {/* merged badge: keeps circling, then becomes a tick */}
      {showBadge && (
        <div className="absolute inset-0 grid place-items-center">
          <div
            className={cn(
              'relative grid size-14 place-items-center rounded-full transition-colors duration-300',
              phase === 'success' ? 'bg-accent' : phase === 'error' ? 'bg-bad' : 'border border-line bg-elevated',
              phase === 'merging' && 'animate-[otp-pop_0.5s_ease-out]',
              phase === 'error' && 'animate-[otp-shake_0.45s_ease-in-out]',
            )}
          >
            {phase === 'success' ? (
              <Check className="size-7 text-accent-ink" style={{ animation: 'otp-pop 0.4s ease-out' }} />
            ) : phase === 'error' ? (
              <X className="size-7 text-on-dark" style={{ animation: 'otp-pop 0.4s ease-out' }} />
            ) : (
              <svg viewBox="0 0 56 56" className="absolute inset-0 text-accent-strong">
                <circle
                  cx="28"
                  cy="28"
                  r="25"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  pathLength={100}
                  strokeDasharray="28 72"
                  style={{ animation: 'otp-trace 0.9s linear infinite' }}
                />
              </svg>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function OtpKeyframes() {
  return (
    <style>{`
      @keyframes otp-trace { to { stroke-dashoffset: -100; } }
      @keyframes otp-pop {
        0% { transform: scale(0.5); opacity: 0; }
        60% { transform: scale(1.08); }
        100% { transform: scale(1); opacity: 1; }
      }
      @keyframes otp-shake {
        0%, 100% { transform: translateX(0); }
        20% { transform: translateX(-5px); }
        40% { transform: translateX(5px); }
        60% { transform: translateX(-4px); }
        80% { transform: translateX(3px); }
      }
    `}</style>
  );
}
